import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import agencyWordmark from '../assets/agency360-logo.png';
import { AppShell } from '../components/AppShell.jsx';
import { AssignmentAckModal } from '../components/AssignmentAckModal.jsx';
import { NotificationsBell } from '../components/NotificationsBell.jsx';
import { SidebarGroup, SidebarHelp, SidebarLink, SidebarMore } from '../components/SidebarNav.jsx';
import { UserMenu } from '../components/UserMenu.jsx';
import { usePortalBranding } from '../branding/usePortalBranding.js';
import { NotificationSocketProvider } from '../context/NotificationSocketContext.jsx';
import { StaffChatSocketProvider } from '../context/StaffChatSocketContext.jsx';
import { OnboardingHelpButton } from '../onboarding/OnboardingProvider.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { ROLES, ROLE_LABELS } from '../utils/roles.js';

export function AdminLayout() {
  const { user } = useAuth();
  const { rootRef, branding } = usePortalBranding('/api/org/branding');
  const navigate = useNavigate();
  const location = useLocation();
  const isOwner = user?.role === ROLES.ADMIN;
  const caps = user?.capabilities ?? {};
  const showHosted = isOwner && (caps.pm2 || caps.docker);
  const showMore = Boolean(
    caps.view_contacts
    || caps.manage_assets
    || caps.faq_forms
    || caps.manage_projects
    || caps.time_tracker
    || showHosted
    || (isOwner && caps.manage_integrations),
  );
  const headerBranding = {
    ...branding,
    name: branding?.name || (isOwner ? 'Agency' : 'Workspace'),
  };

  return (
    <NotificationSocketProvider>
      <StaffChatSocketProvider>
        <AppShell
          rootRef={rootRef}
          branding={headerBranding}
          wordmarkSrc={agencyWordmark}
          subtitle="SaaS Admin Portal"
          searchPlaceholder="Search clients, projects, or anything…"
          onSearch={(term) => {
            const q = encodeURIComponent(term);
            if (location.pathname.startsWith('/admin/clients')) {
              navigate(`/admin/clients?search=${q}`);
              return;
            }
            if (location.pathname.startsWith('/admin/contacts')) {
              navigate(`/admin/contacts?q=${q}`);
              return;
            }
            if (location.pathname.startsWith('/admin/tasks')) {
              navigate(`/admin/tasks?q=${q}`);
              return;
            }
            if (location.pathname.startsWith('/admin/forms')) {
              navigate(`/admin/forms?q=${q}`);
              return;
            }
            navigate(`/admin/projects?q=${q}`);
          }}
          nav={
            <>
              <SidebarLink to="/admin" label="Dashboard" end icon="home" tourId="tour-nav-dashboard" />
              {caps.manage_clients ? (
                <SidebarLink to="/admin/clients" label="Clients" icon="clients" tourId="tour-nav-clients" />
              ) : null}
              {caps.manage_projects ? (
                <SidebarLink to="/admin/projects" label="Projects" icon="projects" tourId="tour-nav-projects" />
              ) : null}
              {caps.manage_projects ? (
                <SidebarLink to="/admin/project-templates" label="Project templates" icon="plans" />
              ) : null}
              {caps.manage_tasks ? (
                <SidebarLink to="/admin/tasks" label="Tasks" icon="work" tourId="tour-nav-work" />
              ) : null}
              {caps.view_mailbox ? (
                <SidebarLink to="/admin/emails" label="Emails" icon="inbox" tourId="tour-nav-inbox" />
              ) : null}
              {caps.view_mailbox ? (
                <SidebarLink to="/admin/meetings" label="Meetings" icon="calendar" />
              ) : null}
              {caps.live_chat ? (
                <SidebarLink to="/admin/chat" label="Live chat" icon="messages" />
              ) : null}
              {isOwner && caps.manage_team ? (
                <SidebarLink to="/admin/team" label="Team" icon="clients" />
              ) : null}
              {isOwner && caps.manage_organization ? (
                <SidebarLink to="/admin/settings" label="Organization" icon="settings" tourId="tour-nav-settings" />
              ) : null}
              {isOwner ? (
                <SidebarLink to="/admin/billing" label="Billing" icon="billing" />
              ) : null}
              {isOwner ? (
                <SidebarLink to="/admin/audit-logs" label="Activity log" icon="activity" />
              ) : null}
              {showMore ? (
                <SidebarMore
                  paths={[
                    '/admin/contacts',
                    '/admin/assets',
                    '/admin/forms',
                    '/admin/pdf-documents',
                    '/admin/time-tracker',
                    '/admin/pm2',
                    '/admin/docker',
                    '/admin/integrations',
                  ]}
                >
                  {caps.view_contacts ? (
                    <SidebarLink to="/admin/contacts" label="Contacts" icon="directory" />
                  ) : null}
                  {caps.manage_assets ? (
                    <SidebarLink to="/admin/assets" label="Media & assets" icon="files" />
                  ) : null}
                  {caps.faq_forms ? (
                    <SidebarLink to="/admin/forms" label="Forms" icon="plans" />
                  ) : null}
                  {caps.manage_projects ? (
                    <SidebarLink to="/admin/pdf-documents" label="PDF documents" icon="files" />
                  ) : null}
                  {caps.time_tracker ? (
                    <SidebarLink to="/admin/time-tracker" label="Time tracker" icon="time" tourId="tour-nav-time" />
                  ) : null}
                  {showHosted ? (
                    <SidebarGroup
                      label="Hosted services"
                      icon="hosts"
                      items={[
                        { to: '/admin/pm2', label: 'PM2', end: true, show: Boolean(caps.pm2) },
                        { to: '/admin/pm2/apps', label: 'npm apps', show: Boolean(caps.pm2) },
                        { to: '/admin/docker', label: 'Docker', show: Boolean(caps.docker) },
                      ]}
                    />
                  ) : null}
                  {isOwner && caps.manage_integrations ? (
                    <SidebarLink to="/admin/integrations" label="Integrations" icon="integrations" />
                  ) : null}
                </SidebarMore>
              ) : null}
            </>
          }
          topbar={
            <div className="topbar-utilities" data-tour="tour-utilities">
              {caps.priority_support ? (
                <span className="priority-chip" title="Priority support">
                  Priority
                </span>
              ) : null}
              <NotificationsBell />
              <OnboardingHelpButton />
              <UserMenu
                profileTo="/admin/profile"
                roleLabel={ROLE_LABELS[user?.role] || (user?.role === ROLES.MANAGER ? 'team admin' : user?.role)}
              />
            </div>
          }
          footer={
            <SidebarHelp href="mailto:support@viib.ca" />
          }
          mainTourId="tour-main-content"
        >
          <Outlet />
          <AssignmentAckModal />
        </AppShell>
      </StaffChatSocketProvider>
    </NotificationSocketProvider>
  );
}
